import { IonIcon } from '@ionic/react';
import { codeSlashOutline, linkOutline, listOutline, checkboxOutline } from 'ionicons/icons';
import type { MarkdownAction } from './markdownFormatting';

interface MarkdownToolbarProps {
  onAction: (action: MarkdownAction) => void;
  disabled?: boolean;
}

type ToolbarItemBase = {
  action: MarkdownAction;
  label: string;
};

// Each button renders either an ionicon or a text glyph (when no faithful ionicon
// exists), never both — the discriminated union keeps the config honest.
type ToolbarItem =
  | (ToolbarItemBase & { icon: string; glyph?: never; glyphClassName?: never })
  | (ToolbarItemBase & { icon?: never; glyph: string; glyphClassName?: string });

// Icons prefer ionicons; buttons without a faithful ionicon use a styled text glyph.
const INLINE_ITEMS: ToolbarItem[] = [
  { action: 'heading', label: 'Encabezado', glyph: 'H', glyphClassName: 'font-bold' },
  { action: 'bold', label: 'Negrita', glyph: 'B', glyphClassName: 'font-bold' },
  { action: 'italic', label: 'Cursiva', glyph: 'I', glyphClassName: 'font-serif italic' },
  { action: 'quote', label: 'Cita', glyph: '”', glyphClassName: 'font-serif font-bold' },
  { action: 'code', label: 'Código', icon: codeSlashOutline },
  { action: 'link', label: 'Enlace', icon: linkOutline },
];

const LIST_ITEMS: ToolbarItem[] = [
  { action: 'unordered-list', label: 'Lista', icon: listOutline },
  { action: 'ordered-list', label: 'Lista numerada', glyph: '1.', glyphClassName: 'font-bold' },
  { action: 'task-list', label: 'Lista de tareas', icon: checkboxOutline },
];

const buttonClassName =
  'flex h-8 w-8 shrink-0 items-center justify-center rounded p-1.5 text-gray-600 hover:bg-gray-100 disabled:opacity-40';

export const MarkdownToolbar = ({ onAction, disabled }: MarkdownToolbarProps) => {
  const renderButton = (item: ToolbarItem) => (
    <button
      key={item.action}
      type="button"
      aria-label={item.label}
      title={item.label}
      disabled={disabled}
      onClick={() => onAction(item.action)}
      className={buttonClassName}
    >
      {item.icon ? (
        <IonIcon icon={item.icon} className="text-lg" />
      ) : (
        <span className={`text-sm leading-none ${item.glyphClassName ?? ''}`}>{item.glyph}</span>
      )}
    </button>
  );

  return (
    <div className="flex items-center gap-1 overflow-x-auto">
      {INLINE_ITEMS.map(renderButton)}
      <span className="mx-1 h-5 w-px shrink-0 bg-gray-300" />
      {LIST_ITEMS.map(renderButton)}
    </div>
  );
};
